import React from 'react';
import useAsyncDataCourier from '../../common/custom-hooks/useAsyncDataCourier';
import MessageCourier from '../../lib/MessageCourier';
import { Federacion } from '../../system/Federacion/Federacion';
import SystemClient from '../../system/SystemClient';
import ComboBoxSearch, { IComboBoxSearch } from '../Common/ComboBoxSearch';
import { IComboBoxOption } from '@fluentui/react';
import ComboBoxInput from '../../lib/FormValidator/Form/ComboBoxInput';
import CustomInputIfc from '../../lib/FormValidator/Form/CustomInputIfc';
import { t } from 'i18next';
import { useTranslation } from 'react-i18next';
import useForceUpdate from '../../common/custom-hooks/useForceUpdate';

interface IFederacionSelector {
  system: SystemClient;
  courier: MessageCourier;
  onSelect: (federacion: Federacion) => void;
  selected: string[];
  required?: boolean;
  all?: boolean;
  onSelectAll?: () => void;
  label?: string;
  onDelete?: () => void;
}

//==========================================================
// Selector independiente
//==========================================================
export const FederacionSelector = (props: IFederacionSelector) => {
  const federaciones = useAsyncDataCourier(
    () => props.system.getFederacionSystem().getListForDropdown(),
    [],
    props.courier
  );

  const getFromKey = (key: string) => {
    if (federaciones) {
      let fed = federaciones.find((f) => f.getId() == key);
      if (props.all && key == '-1' && props.onSelectAll) {
        console.log('paso');
        return props.onSelectAll();
      }
      if (fed) {
        return props.onSelect(fed);
      }
    }
  };

  const federacionOptions = React.useMemo(() => {
    let ret: IComboBoxOption[] = [];
    if (federaciones) {
      ret = federaciones.map((entity: Federacion) => {
        const federacionLabel = '(' + entity.getSigla() + ')';
        const label = String(entity.getDenominacion()!) + ' ' + federacionLabel;

        return { key: entity.getId()!, text: String(label), data: entity };
      });
      if (props.all) {
        ret = ret.concat({ key: '-1', text: 'Todas', data: undefined });
      }
    }
    return ret;
  }, [federaciones]);
  return (
    <ComboBoxSearch
      onSelectOption={(opt) => getFromKey(String(opt.key))}
      selected={props.selected}
      options={federacionOptions}
      comboProps={
        props.required
          ? { label: props.label ? props.label : 'Federación (*)', options: federacionOptions }
          : { label: props.label ? props.label : 'Federación', options: federacionOptions }
      }
      onDelete={props.onDelete}
    ></ComboBoxSearch>
  );
};

//==========================================================
// Selector Federacion, integrado con formulario FederacionSelectorInput
//==========================================================

interface IFederacionSelectorInput {
  system: SystemClient;
  courier: MessageCourier;
  input: CustomInputIfc;
  hideLabel?: boolean;
  onBlurCustomInput: (customInput: CustomInputIfc) => void;
  providedList?: Federacion[];
  federacionId?: number | undefined;
  onChangeExtended?: (federacion: Federacion) => void;
  disabled?: boolean;
  label?: string;
}

export const FederacionSelectorInput = (props: IFederacionSelectorInput) => {
  const { t, i18n } = useTranslation();
  const [federacion, setCurrentFederacion] = React.useState<Federacion | undefined>(undefined);
  const [federacionOptions, setFederacionOptions] = React.useState<IComboBoxOption[]>([]);
  const [federacionFilteredByPlayer, setFederacionFilteredByPlayer] = React.useState<string>();

  const federaciones = useAsyncDataCourier(
    () =>
      props.providedList
        ? Promise.resolve(props.providedList)
        : props.system.getFederacionSystem().getListForDropdown(),
    [],
    props.courier
  );

  //==========================================================
  // Definimos key and label
  //==========================================================
  const mapFederacionOptions = (federaciones: Federacion[]) => {
    const opciones = federaciones.map((entity: Federacion) => {
      return { key: entity.getId()!, text: getLabelFromFederacion(entity), data: entity };
    });
    setFederacionOptions(opciones);
  };

  //==========================================================
  // Define valor por defecto (input)
  //==========================================================
  const selectDefaultFederacion = (id: string) => {
    setCurrentFederacion(
      federaciones ? federaciones.find((entity: Federacion) => entity.getId() === id) : undefined
    );
  };

  const forceUpdate = useForceUpdate();

  //==========================================================
  // Define custom label ( Federacion + Federación)
  //==========================================================
  const getLabelFromFederacion = (federacion: Federacion) => {
    const federacionLabel = '(' + federacion.getSigla() + ')';
    const label = String(federacion.getDenominacion()!) + ' ' + federacionLabel;
    return label;
  };

  React.useEffect(() => {
    forceUpdate();
  }, [federacion]);

  React.useEffect(() => {
    selectDefaultFederacion(props.input.getValue());
    mapFederacionOptions(federaciones ? federaciones : []);
  }, [federaciones, federacion]);

  //==========================================================
  // Define Label de federacion por defecto
  //==========================================================
  const getStartingSelectionFederacion = () => {
    let ret = '';
    if (federaciones) {
      const _federacion = federaciones.find((c) => c.getId() === props.input.getValue());
      if (_federacion) ret = String(_federacion.getId());
    }
    return ret;
  };

  const getIdFromFederacion = (federacion: Federacion) => {
    const id = federacion.getId();
    if (id !== null) {
      return id;
    } else {
      return '';
    }
  };

  React.useEffect(() => {
    let ret = '';
    if (federaciones && props.federacionId) {
      console.log(props.federacionId);
      const _federacion = federaciones.find((f) => Number(f.getId()) === Number(props.federacionId));
      if (_federacion) ret = getIdFromFederacion(_federacion);
      console.log(_federacion?.getId());
      props.input.setValue(_federacion?.getId());
      console.log(federaciones);
      setFederacionFilteredByPlayer(ret);
    }
  }, [props.federacionId, federaciones]);

  const onChangeExtended = () => {
    const _federacion = federaciones?.find((c) => c.getId() === props.input.getValue());
    if (_federacion && props.onChangeExtended) {
      props.onChangeExtended(_federacion);
    }
  };

  return (
    <>
      {federaciones && (
        <ComboBoxInput
          input={props.input}
          label={props.hideLabel ? '' : props.label ? props.label : t('Federación (*)')}
          onBlur={props.onBlurCustomInput}
          options={federacionOptions ? federacionOptions : []}
          onChangeExtended={onChangeExtended}
          disabled={props.disabled}
          selectedKey={
            props.federacionId ? federacionFilteredByPlayer : getStartingSelectionFederacion()
            // currentFederacion?.getNombre() ? String(currentFederacion?.getNombre()) : undefined
          }
        />
      )}
    </>
  );
};
